import { ComposerComponent, ContextComposer, useErpInfosContext } from "@bundles/parts/components/ListV2/ContextProvider"
import { Article, ArticleErpInfo } from "@tm/models"
import { Box, styled } from "@tm/components"
import { useState } from "react"
import { SkeletonArticleItemTile } from "@bundles/parts/components/ListV2/components/SkeletonArticleItemTile"
import { BasketQuantitiesProvider, WholesalerBasketQuantitiesProvider } from "@bundles/parts/components/ListV2/ContextProvider/BasketQuantities"
import { NotesProvider } from "@bundles/parts/components/ListV2/ContextProvider/Notes"
import { ProductGroupRepairTimesProvider } from "@bundles/parts/components/ListV2/ContextProvider/ProductGroupRepairTimes"
import { SupplierLogosProvider } from "@bundles/parts/components/ListV2/ContextProvider/SupplierLogos"
import { TradeReferenceProvider } from "@bundles/parts/components/ListV2/ContextProvider/TradeReferences"
import { BasketMemoProvider } from "@bundles/parts/components/ListV2/ContextProvider/BasketMemo"
import { EmptyListParamsProvider } from "@bundles/parts/components/ListV2/ContextProvider/ListParams"
import { CarouselActionsProvider } from "./ContextProviders/CarouselActions"
import { CarouselArticlesProvider } from "./ContextProviders/Articles"
import { CarouselConfigurationProvider } from "./ContextProviders/CarouselConfiguration/CarouselConfigurationContext"
import { CarouselOptionsProvider } from "./ContextProviders/CarouselOptions"
import { MultiErpInfosProvider } from "./ContextProviders/ErpInfos"
import { CarouselArticleItem } from "./ContextProviders/CarouselArticleItem/CarouselArticleItem"
import { CarouselWholesalerArticleItem } from "./ContextProviders/CarouselArticleItem/CarouselWholesalerArticleItem"
import { TraderArticle } from "../../helpers"

const BASE_TILE_HEIGHT = 460
const ERP_AREA_ICON_HEIGHT_DIFF = 25

function getGridTemplateRowsHeight(hasNoErpAreaIcons?: boolean) {
    let height = BASE_TILE_HEIGHT
    if (hasNoErpAreaIcons) {
        height -= ERP_AREA_ICON_HEIGHT_DIFF
    }
    return height
}

type TileWrapperProps = {
    itemMinWidth: number
    itemSpacing: number
    hasNoErpAreaIcons?: boolean
}

const TileWrapper = styled(Box, {
    shouldForwardProp: (prop: string) => !["itemMinWidth", "itemSpacing", "hasNoErpAreaIcons"].includes(prop),
})<TileWrapperProps>(({ itemMinWidth, itemSpacing, hasNoErpAreaIcons }) => ({
    display: "grid",
    // dimensions transferred from the mockup (https://zpl.io/WQQnr0j)
    gridTemplateRows: getGridTemplateRowsHeight(hasNoErpAreaIcons),
    gridTemplateColumns: `repeat(auto-fill, minmax(${itemMinWidth}px, 1fr))`,
    columnGap: `${itemSpacing}px`,
    padding: "0.8rem 0 0.2rem 0",
    alignItems: "stretch",
    justifyContent: "center",
}))

type Props = TileWrapperProps & {
    parts: (Article | TraderArticle)[]
    workTaskId: string
    requestPartList(showAddToBasketMessage?: boolean): void
    advertisementsLoading: boolean
    itemAmount: number
}

function OfferTilesComponent(props: Props) {
    const { parts, itemMinWidth, itemSpacing, workTaskId, requestPartList, advertisementsLoading, itemAmount } = props
    const { hasNoErpAreaIcons } = useErpInfosContext()

    return (
        <TileWrapper itemMinWidth={itemMinWidth} itemSpacing={itemSpacing} hasNoErpAreaIcons={hasNoErpAreaIcons}>
            {parts.map((part, index) => {
                if ("isTraderArticle" in part) {
                    return (
                        <CarouselWholesalerArticleItem
                            key={part.id}
                            article={{ itemId: part.id, wholesalerArticleNumber: part.traderArticleNumber }}
                            workTaskId={workTaskId}
                            requestPartList={requestPartList}
                        />
                    )
                }
                return (
                    <CarouselArticleItem
                        key={part.id}
                        article={part}
                        workTaskId={workTaskId}
                        articleOrder={index}
                        requestPartList={requestPartList}
                    />
                )
            })}
            {advertisementsLoading &&
                Array.from(Array(itemAmount || 2).keys()).map((_, index) => (
                    <SkeletonArticleItemTile key={index} animation="pulse" tileViewNoTyres />
                ))}
        </TileWrapper>
    )
}

export function OfferTiles(props: Props) {
    const { parts } = props
    const [erpInfos, setErpInfos] = useState<ArticleErpInfo[]>([])

    const components: ComposerComponent[] = [
        [CarouselConfigurationProvider],
        [EmptyListParamsProvider, { listType: "carousel" }],
        [BasketMemoProvider],
        [CarouselArticlesProvider, { parts }],
        [TradeReferenceProvider, { isEnabled: true }],
        [MultiErpInfosProvider, { erpInfos, setErpInfos }],
        [BasketQuantitiesProvider, { isEnabled: true }],
        [WholesalerBasketQuantitiesProvider, { isEnabled: true }],
        [ProductGroupRepairTimesProvider, { isEnabled: true }],
        [NotesProvider, { isEnabled: true }],
        [CarouselOptionsProvider],
        [SupplierLogosProvider, { isEnabled: true }],
        [CarouselActionsProvider],
    ]

    return (
        <ContextComposer components={components}>
            <OfferTilesComponent {...props} />
        </ContextComposer>
    )
}
